import { useEffect, useState } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    let mounted = true

    const checkUser = async () => {
      try {
        console.log('Verificando sessão do utilizador...')
        const { data: { session } } = await supabase.auth.getSession()
        
        if (!mounted) return

        if (session?.user) {
          console.log('Sessão encontrada:', session.user)
          setUser(session.user)
        } else {
          console.log('Nenhuma sessão encontrada')
          setUser(null)
        }
      } catch (error) {
        console.error('Erro ao verificar autenticação:', error)
        setUser(null)
      } finally {
        if (mounted) {
          setLoading(false)
          setInitialized(true)
        }
      }
    }

    // Initial check
    checkUser()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Evento de autenticação:', event)
      if (!mounted) return

      if (session?.user) {
        console.log('Utilizador autenticado:', session.user)
        setUser(session.user)
      } else {
        console.log('Utilizador não autenticado')
        setUser(null)
      }
      
      if (!initialized) {
        setInitialized(true)
        setLoading(false)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    console.log('Tentando fazer login...')
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) {
        console.error('Erro no login:', error)
        throw error
      }
      
      if (!data?.user) {
        console.error('Nenhum utilizador retornado após login')
        throw new Error('Não foi possível obter os dados do utilizador')
      }

      console.log('Login bem-sucedido:', data.user)

      // Aguardar até que o estado do usuário seja atualizado
      await new Promise<void>((resolve) => {
        const unsubscribe = supabase.auth.onAuthStateChange((event, session) => {
          if (event === 'SIGNED_IN' && session?.user?.id === data.user.id) {
            console.log('Estado do usuário atualizado após login')
            unsubscribe.data.subscription.unsubscribe()
            resolve()
          }
        })

        // Timeout de segurança após 5 segundos
        setTimeout(() => {
          unsubscribe.data.subscription.unsubscribe()
          resolve()
        }, 5000)
      })

      return data.user
    } catch (error) {
      console.error('Erro durante o login:', error)
      throw error
    }
  }

  const signOut = async () => {
    console.log('Iniciando processo de signOut...');
    try {
      // Primeiro limpar o estado local
      setUser(null);
      setInitialized(false);
      setLoading(false);
      console.log('Estado local limpo');

      // Desinscrever de todas as subscrições do Supabase
      const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {});
      subscription.unsubscribe();
      console.log('Subscrições canceladas');

      // Fazer logout no Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Erro no signOut:', error);
        throw error;
      }

      // Aguardar até que o estado seja completamente limpo
      await new Promise<void>((resolve) => {
        const unsubscribe = supabase.auth.onAuthStateChange((event) => {
          if (event === 'SIGNED_OUT') {
            console.log('Estado de autenticação atualizado para SIGNED_OUT');
            unsubscribe.data.subscription.unsubscribe();
            resolve();
          }
        });
      
        // Timeout de segurança após 3 segundos
        setTimeout(() => {
          unsubscribe.data.subscription.unsubscribe();
          resolve();
        }, 3000);
      });
    
      // Verificar se a sessão foi realmente limpa
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        console.error('Sessão ainda existe após logout');
        throw new Error('Falha ao limpar sessão');
      }
    
      console.log('SignOut concluído com sucesso');
    } catch (error) {
      console.error('Erro durante o signOut:', error);
      // Garantir que o estado local está limpo mesmo em caso de erro
      setUser(null);
      setInitialized(false);
      setLoading(false);
      throw error;
    }
  };

  return {
    user,
    loading,
    initialized,
    signIn,
    signOut
  }
}